import { useEffect, useState } from 'react'
import { Button, Chip, Input, TextArea } from '@heroui/react'
import { api, type StoredLayout } from '../../lib/api'
import { freeName, kindsOffered, problemsAt, unitIndexOf } from '../../lib/equipment'
import { MARKS_AND_OUTCOMES } from '../../lib/features'
import { designChoices } from '../../lib/kioskProfile'
import { uuidv7 } from '../../lib/profilesDoc'
import { explain } from '../../lib/staffErrors'
import type { StaffKey } from '../../lib/staffText'
import { passUnsaved, useUnsaved } from '../../lib/unsaved'
import type { OperationProfileRow, ProfileProblem, ProfileUnitKind } from '../../lib/types'
import { Icon, type IconName } from '../../ui/icons'
import { useMayRead } from '../../ui/Session'
import { useStaffText } from '../../ui/StaffText'
import { Confirm, useFlash } from '../sectionKit'
import { DangerCard, EditLayout, FieldLabel, FormSection, IconTile, Notice, PageHead, SaveActions, SummaryCard } from '../settingsKit'
import { AskBar } from '../setupKit'
import { UnitSheet } from './UnitSheet'
import { useEquipmentDoc } from './useEquipmentDoc'
import '../../styles/equipment.css'

const kindKey = (kind: ProfileUnitKind) => `eq.kind.${kind}` as StaffKey
const oneKey = (kind: ProfileUnitKind) => `eq.kind.one.${kind}` as StaffKey
const KIND_ICON: Record<ProfileUnitKind, IconName> = {
  service_point: 'counter',
  kiosk: 'machine',
  display: 'designs',
  desk_display: 'layers',
  feedback: 'message',
}

/** One equipment profile: its name and units, saved whole, with delete kept apart. */
export function EquipmentPage({
  id,
  readOnly,
  profiles,
  onBack,
  onProfile,
  onOpen,
}: {
  id: string
  readOnly: boolean
  /** The organisation's operation profiles, to name the one this belongs to. */
  profiles: OperationProfileRow[]
  onBack: () => void
  /** Told which operation profile this one belongs to, so the list comes back on it. */
  onProfile?: (operationProfileId: string) => void
  /** Opens another equipment profile's page: the copy Duplicate made. */
  onOpen?: (id: string) => void
}) {
  const { lang, say } = useStaffText()
  const eq = useEquipmentDoc(id)
  const { flash, toast } = useFlash()
  const [saved, setSaved] = useState({ name: '', description: '' })
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [siblings, setSiblings] = useState<number | null>(null)
  const [unitAt, setUnitAt] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [layouts, setLayouts] = useState<StoredLayout[]>([])
  const readsLayouts = useMayRead('layouts')
  const row = eq.row

  // Without the designs the pickers offer only Default.
  useEffect(() => {
    if (!readsLayouts) return
    api
      .layouts()
      .then((r) => setLayouts(r.layouts))
      .catch(() => setLayouts([]))
  }, [readsLayouts])

  // A (re)read of the profile resets the details to what is stored.
  useEffect(() => {
    if (!row) return
    setSaved({ name: row.name, description: row.description })
    setName(row.name)
    setDescription(row.description)
  }, [row])

  const opId = row?.operationProfileId
  useEffect(() => {
    if (!opId) return
    onProfile?.(opId)
    api
      .equipmentProfiles(opId)
      .then((r) => setSiblings(r.equipmentProfiles.length))
      .catch(() => setSiblings(null))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opId])

  const detailsDirty = name !== saved.name || description !== saved.description
  const dirty = eq.dirty || detailsDirty
  useUnsaved(dirty)

  // Leaving the page with unsaved edits asks the browser's own question.
  useEffect(() => {
    if (!dirty) return
    const ask = (e: BeforeUnloadEvent) => e.preventDefault()
    addEventListener('beforeunload', ask)
    return () => removeEventListener('beforeunload', ask)
  }, [dirty])

  const save = async () => {
    if (!name.trim()) return
    setError('')
    if (detailsDirty) {
      setBusy(true)
      try {
        const next = { name: name.trim(), description: description.trim() }
        await api.updateEquipmentProfile(id, next)
        setSaved(next)
        setName(next.name)
        setDescription(next.description)
      } catch (err) {
        setError(explain(err, lang, 'common.unreachable'))
        return
      } finally {
        setBusy(false)
      }
    }
    if (eq.dirty && !(await eq.save())) return
    flash(say('eq.tab.saved'))
  }

  const discard = () => {
    setName(saved.name)
    setDescription(saved.description)
    setError('')
    void eq.reload()
  }

  const remove = async () => {
    setBusy(true)
    setError('')
    try {
      await api.deleteEquipmentProfile(id)
      passUnsaved()
      onBack()
    } catch (err) {
      setError(explain(err, lang, 'common.unreachable'))
      setBusy(false)
    }
  }

  // Copies what is stored; the copy's page opens, named "Copy of …" by the server.
  const duplicate = async () => {
    setBusy(true)
    setError('')
    try {
      const r = await api.duplicateEquipmentProfile(id)
      onOpen?.(r.equipmentProfile.id)
    } catch (err) {
      setError(explain(err, lang, 'common.unreachable'))
    } finally {
      setBusy(false)
    }
  }

  const addUnit = (kind: ProfileUnitKind) =>
    eq.setDoc((d) => d && { ...d, units: [...d.units, { id: uuidv7(), kind, name: freeName(d, say(oneKey(kind))), defaultCount: 1, settings: {} }] })

  const doc = eq.doc
  const failed = eq.problems.filter((p) => !p.warning)
  const unpinned = eq.problems.filter((p) => {
    const i = unitIndexOf(p.path)
    return i == null || !doc || i >= doc.units.length
  })
  const noticed: ProfileProblem[] = [...new Set([...failed.slice(0, 1), ...unpinned])]
  const opName = profiles.find((p) => p.id === opId)?.name
  const why = row && (row.branchCount > 0 ? say('eq.tab.inUse', { n: row.branchCount }) : siblings === 1 ? say('eq.tab.onlyOne') : '')

  const crumbs = [{ label: say('admin.section.equipment'), onOpen: onBack }]
  if (!row || !doc) {
    return (
      <>
        <PageHead crumbs={crumbs} title={row?.name ?? say('eq.tab.profiles')} />
        {eq.error ? <Notice>{eq.error}</Notice> : <p className="empty">{say('common.loading')}</p>}
      </>
    )
  }

  return (
    <>
      <PageHead
        crumbs={crumbs}
        title={saved.name}
        line={saved.description || undefined}
        actions={
          !readOnly && (
            <>
              {onOpen && (
                <Button variant="ghost" isDisabled={dirty || busy || eq.busy} onPress={() => void duplicate()}>
                  <Icon name="copy" size={16} /> {say('eq.tab.duplicate')}
                </Button>
              )}
              <SaveActions dirty={dirty && name.trim() !== ''} busy={busy || eq.busy} onDiscard={discard} onSave={() => void save()} />
            </>
          )
        }
      />
      {toast}
      {eq.stale && (
        <AskBar
          className="eq-bar"
          role="alert"
          title={say('eq.tab.stale')}
          actions={
            <Button variant="secondary" size="sm" onPress={discard}>
              {say('eq.tab.reload')}
            </Button>
          }
        />
      )}
      {(error || eq.error) && <Notice className="eq-bar">{error || eq.error}</Notice>}
      {noticed.length > 0 && (
        <Notice tone={failed.length ? 'danger' : 'warning'} className="eq-bar">
          {noticed.map((p, k) => (
            <span key={k} className="eq-bar__line">
              {p.message}
            </span>
          ))}
        </Notice>
      )}

      <EditLayout
        side={
          <>
            <SummaryCard
              band={say('settings.equipment.band')}
              tile={<IconTile icon="machine" size="xl" />}
              name={saved.name}
              sub={opName}
              state={row.branchCount > 0 ? say('eq.tab.inUse', { n: row.branchCount }) : undefined}
              stats={[
                { label: say('settings.equipment.units'), value: doc.units.length },
                { label: say('settings.col.branches'), value: row.branchCount },
              ]}
            />
            {!readOnly && (
              <DangerCard
                title={say('settings.equipment.deleteTitle')}
                why={why || undefined}
                line={say('settings.equipment.deleteLine')}
                action={
                  why ? (
                    <Button variant="danger-soft" isDisabled>
                      {say('common.delete')}
                    </Button>
                  ) : (
                    <Confirm
                      ask={say('common.delete')}
                      question={say('eq.tab.deleteAsk')}
                      yes={say('common.delete')}
                      keep={say('common.keep')}
                      busy={busy}
                      onYes={() => void remove()}
                    />
                  )
                }
              />
            )}
          </>
        }
      >
        <FormSection id="eq-details" icon="edit" title={say('settings.equipment.details')} line={say('settings.equipment.detailsLine')}>
          <label className="field">
            <span>
              <FieldLabel text={say('common.name')} required />
            </span>
            <Input fullWidth value={name} required disabled={readOnly} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="field">
            <span>
              <FieldLabel text={say('eq.tab.description')} optional />
            </span>
            <TextArea fullWidth rows={2} value={description} disabled={readOnly} onChange={(e) => setDescription(e.target.value)} />
          </label>
        </FormSection>

        {kindsOffered(doc.units, MARKS_AND_OUTCOMES).map((kind) => {
          const units = doc.units.map((u, i) => [u, i] as const).filter(([u]) => u.kind === kind)
          return (
            <FormSection
              key={kind}
              id={`eq-kind-${kind}`}
              icon={KIND_ICON[kind]}
              title={say(kindKey(kind))}
              status={units.length === 0 ? say('eq.tab.noneOfKind') : undefined}
              aside={
                !readOnly && (
                  <Button variant="secondary" size="sm" onPress={() => addUnit(kind)}>
                    <Icon name="plus" size={16} /> {say('eq.tab.unit.add')}
                  </Button>
                )
              }
            >
              {units.length > 0 && (
                <ul className="eq-units">
                  {units.map(([u, i]) => {
                    const ps = problemsAt(eq.problems, `units[${i}]`)
                    return (
                      <li key={u.id}>
                        <button type="button" className="eq-units__row" onClick={() => setUnitAt(i)}>
                          <span className="eq-units__name">{u.name}</span>
                          {ps.length > 0 && (
                            <Chip size="sm" variant="soft" color="warning" className="eq-units__problem" title={ps.map((p) => p.message).join('\n')}>
                              {ps[0]!.message}
                            </Chip>
                          )}
                          <Chip size="sm" variant="secondary" className="eq-units__count" aria-label={say('eq.tab.count')}>
                            {u.defaultCount}
                          </Chip>
                          <Icon name="right" size={16} />
                        </button>
                      </li>
                    )
                  })}
                </ul>
              )}
            </FormSection>
          )
        })}
      </EditLayout>

      {unitAt != null && doc.units[unitAt] && (
        <UnitSheet
          unit={doc.units[unitAt]}
          units={doc.units}
          inherited={eq.kindDefaults[doc.units[unitAt].kind] ?? {}}
          choices={designChoices(layouts, doc.units[unitAt].kind, row.operationProfileId)}
          readOnly={readOnly}
          onClose={() => setUnitAt(null)}
          onApply={(next) => {
            eq.setDoc((d) => d && { ...d, units: d.units.map((u, i) => (i === unitAt ? next : u)) })
            setUnitAt(null)
          }}
          onDelete={() => {
            eq.setDoc((d) => d && { ...d, units: d.units.filter((_, i) => i !== unitAt) })
            setUnitAt(null)
          }}
        />
      )}
    </>
  )
}
